// https://github.com/braiekhazem/react-kanban-kit/tree/main
import React, { useCallback, useEffect, useState, useRef, useMemo, lazy, Suspense } from 'react'
import { Grid } from '@material-ui/core'

import { Loader } from '@refera/ui-web'

import { navigate } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import { Kanban } from 'react-kanban-kit'

import humps from 'humps'

import HeaderTitle from '_/components/header-title'
import useRolePermission from '_/hooks/use-role-permission'
import { useToast } from '_/hooks/use-toast'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { ROUTES } from '_/utils/constants'

import initialData from './initial-data.json'

import { SkeletonCard, SkeletonColumnHeader, SKELETON_DATA } from './components/skeleton'
import { ColumnHeader } from './components/column-header'
import { buildKanbanColumns, cloneObj, formatDataForKanban } from './utils'
import { KanbanCard } from './components/card'

import { formatErrorMessage } from '../finance/utils/FormatErrorMessage'

import {
  getKanbanServiceOrdersSelector,
  getSystemParametersSelector,
} from '_/modules/service-orders/selectors'
import {
  getKanbanCardInfo,
  getKanbanServiceOrders,
  getSystemParameters,
} from '_/modules/service-orders/actions'
import { userSelector } from '_/modules/authentication/selectors'
import { updateUser } from '_/modules/authentication/actions'

import { HeaderButtons } from './components/header-buttons'
import { useUrlParams } from '_/hooks/use-url-params'

import {
  useStyles,
  COLUMN_WRAPPER_STYLE,
  COLUMN_STYLE,
  MINIMIZED_COLUMN_STYLE,
  MINIMIZED_COLUMN_WRAPPER_STYLE,
  ROOT_STYLE,
  COLUMN_HIGHLIGHT_DIM_STYLE,
  COLUMN_HIGHLIGHT_ALLOWED_STYLE,
} from './styles'
import { ColumnFooter } from './components/column-footer'
import useDragHighlight from './hooks/use-drag-highlight'
import { KANBAN_ACTIONS_SYSTEM_PARAM_NAME } from './constants'
import { useKanbanServiceOrderActions } from './hooks/use-kanban-service-order-actions'

/**
 * @typedef {Object} CardMoveInfo
 * @property {string} cardId - The ID of the card being moved.
 * @property {string} fromColumnId - The ID of the column the card is moving from.
 * @property {string} toColumnId - The ID of the column the card is moving to.
 * @property {string|null} taskAbove - The ID of the task above the card in the new column, or null if no task is above.
 * @property {string|null} taskBelow - The ID of the task below the card in the new column, or null if no task is below.
 * @property {number} position - The new position of the card in the new column.
 */

/**
 * @typedef {Object} DndState
 * @property {'idle' | 'is-dragging' | 'is-dragging-and-left-self' | 'is-over' | 'preview'} type
 * @property {DOMRect} [dragging] Kanban card dnd dragging rect
 * @property {Edge} [closestEdge] Kanban card dnd closest edge
 */

const CardMoveModal = lazy(() =>
  import('./components/card-move-modal').then(module => ({ default: module.CardMoveModal }))
)

const renderCard = ({ data, minimizedColumns }) => {
  const { content, isSkeleton } = data
  if (minimizedColumns.has(data?.parentId)) return null

  if (isSkeleton) return <SkeletonCard />

  return (
    <Grid style={{ marginRight: '8px' }}>
      <KanbanCard serviceOrder={content} />
    </Grid>
  )
}

export function KanbanServiceOrder() {
  const styles = useStyles()
  const dispatch = useDispatch()
  const systemParameters = useSelector(getSystemParametersSelector)
  const kanbanColumnActionsActive = useMemo(() => {
    return (
      systemParameters?.find(param => param.name === KANBAN_ACTIONS_SYSTEM_PARAM_NAME)?.isActive ||
      false
    )
  }, [systemParameters])

  const { getAllParams, search, updateParam } = useUrlParams()
  const { showToast } = useToast()
  // Ref to control the delay timeout
  const movingCardInfo = useRef({})
  const updateTimeoutRef = useRef(null)

  const { isSAAS, checkIfUserDoesNotPermission } = useRolePermission()

  const user = useSelector(userSelector)
  const kanbanResults = useSelector(getKanbanServiceOrdersSelector)

  const userMinimizedColumns = useMemo(() => {
    return user?.kanbanParams?.minimizedColumns?.map(column => humps.camelize(column)) || []
  }, [user])

  const [loading, setLoading] = useState(true)
  const [dataSource, setDataSource] = useState(() => cloneObj(initialData))
  const [hasPermissionToViewPage, setHasPermissionToViewPage] = useState(false)
  const [cardMoveModal, setCardMoveModal] = useState({
    open: false,
    cardInfo: {},
  })

  // Controls visual highlighting during card dragging
  const {
    cardMoving,
    dragHighlight,
    setDragHighlight,
    clearDragHighlight,
    attachDragEndListeners,
  } = useDragHighlight()

  const fetchAndUpdateKanbanCardData = useCallback(async () => {
    const { serviceOrderId, fromColumn, toColumn } = movingCardInfo.current

    setLoading(true)
    await dispatch(getKanbanCardInfo({ serviceOrderId, fromColumn, toColumn }))
      .catch(err => showToast({ type: 'error', message: formatErrorMessage(err) }))
      .finally(() => {
        setLoading(false)
        movingCardInfo.current = {}
      })
  }, [dispatch, showToast, movingCardInfo])

  const { setActionsManager, ManagerComponent } = useKanbanServiceOrderActions({
    moveCardFn: fetchAndUpdateKanbanCardData,
  })

  /**
   *
   * @param {Object} info Kanban card dnd info
   * @param {Object} [info.column] Kanban card dnd column
   * @param {Object} [info.card] Kanban dnd card
   * @param {DndState} [info.state] Kanban dnd type
   */
  function handleCardDNDChange(info) {
    if (!kanbanColumnActionsActive) return

    const currentCardId = cardMoving.current?.id
    if (!currentCardId && info?.state?.type === 'is-dragging') {
      cardMoving.current = {
        id: info?.card?.id,
      }

      // Activate highlight from source column allowed moves
      const allowedMoves = info?.column?.allowedMoves || []
      const allowedTargetIds = new Set(allowedMoves.map(slug => humps.camelize(slug)))
      setDragHighlight({
        active: true,
        fromColumnId: info?.column?.id,
        allowedTargetIds,
      })

      // Clear highlight reliably when the interaction ends or is canceled
      attachDragEndListeners()
    }
  }

  const [minimizedColumns, setMinimizedColumns] = useState(new Set(userMinimizedColumns))

  const { useNewServiceOrder = false } = user?.params || {}

  const configMap = {
    card: {
      render: props => renderCard({ ...props, minimizedColumns }),
      isDraggable: true,
    },
  }

  const toggleColumnMinimized = useCallback(
    async columnId => {
      let newSet = new Set()
      setMinimizedColumns(prev => {
        newSet = new Set(prev)
        if (newSet.has(columnId)) newSet.delete(columnId)
        else newSet.add(columnId)
        return newSet
      })

      // Clear previous timeout if it exists
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current)

      // 3 seconds timeout to update user minimized columns
      updateTimeoutRef.current = setTimeout(async () => {
        const decamelizedColumns = Array.from(newSet).map(column => humps.decamelize(column))
        await dispatch(updateUser({ kanban_params: { minimized_columns: decamelizedColumns } }))
      }, 3000)
    },
    [dispatch, showToast]
  )

  const verifyPermission = useCallback(() => {
    const shouldRedirectUser = checkIfUserDoesNotPermission(
      PERMISSIONS.KANBAN,
      PERMISSIONS_ACTIONS.READ
    )

    if (shouldRedirectUser) navigate(ROUTES.ACCESS_DENIED)
    else setHasPermissionToViewPage(true)
  }, [checkIfUserDoesNotPermission])

  const fetchKanbanData = useCallback(async () => {
    const filters = getAllParams()
    const params = { ...filters }
    setLoading(true)
    await dispatch(getKanbanServiceOrders(params))
      .catch(err => showToast({ type: 'error', message: formatErrorMessage(err) }))
      .finally(() => setLoading(false))
  }, [getAllParams, dispatch, showToast])

  function fillColumns() {
    if (!kanbanResults) return

    const kanbanColumns = buildKanbanColumns(kanbanResults)
    // Build the complete dataSource in a single operation
    setDataSource(prev => formatDataForKanban({ prevData: prev, kanbanColumns, kanbanResults }))
  }

  const setupPage = useCallback(async () => {
    verifyPermission()
    updateParam({
      paramName: 'viewOnlyMyCompanyTickets',
      paramValue: user?.viewOnlyMyCompanyTickets,
    })
    dispatch(getSystemParameters({ name: KANBAN_ACTIONS_SYSTEM_PARAM_NAME }))
    return () => updateTimeoutRef.current && clearTimeout(updateTimeoutRef.current)
  }, [verifyPermission, updateTimeoutRef, user, updateParam])

  function renderColumnHeader(column) {
    return loading ? (
      <SkeletonColumnHeader column={column} minimizedColumns={minimizedColumns} />
    ) : (
      <ColumnHeader
        column={column}
        minimizedColumns={minimizedColumns}
        toggleColumnMinimized={toggleColumnMinimized}
      />
    )
  }

  function columnWrapperStyle(column) {
    return minimizedColumns.has(column.id) ? MINIMIZED_COLUMN_WRAPPER_STYLE : COLUMN_WRAPPER_STYLE
  }

  function columnStyle(column) {
    const baseStyle = minimizedColumns.has(column.id) ? MINIMIZED_COLUMN_STYLE : COLUMN_STYLE

    if (!dragHighlight.active) return baseStyle

    const isAllowed = dragHighlight.allowedTargetIds.has(column.id)
    const dimmed = { ...baseStyle, ...COLUMN_HIGHLIGHT_DIM_STYLE }
    return isAllowed ? { ...dimmed, ...COLUMN_HIGHLIGHT_ALLOWED_STYLE } : dimmed
  }

  /**
   * @param {CardMoveInfo} move - The information about the card move.
   * @returns {void}
   */
  function onCardMove(move) {
    const { cardId } = move

    const fromColumnId = humps.decamelize(move.fromColumnId)
    const toColumnId = humps.decamelize(move.toColumnId)

    movingCardInfo.current = {
      serviceOrderId: cardId.split('-')[1],
      fromColumn: move.fromColumnId,
      toColumn: move.toColumnId,
    }

    if (fromColumnId === toColumnId) return

    const cardMetadata = kanbanResults?.[move.fromColumnId]?.metadata || {}
    const allowedMoves = cardMetadata?.allowedMoves || []
    const isAllowed = isSAAS && allowedMoves.includes(toColumnId) // Only SAAS can move cards between columns

    if (kanbanColumnActionsActive && isAllowed) {
      // Clear any active highlight on successful move
      clearDragHighlight()
      setActionsManager({ from: fromColumnId, to: toColumnId, cardId })
    } else {
      const { helpLink, additionalInformation } = cardMetadata

      if (helpLink || additionalInformation) {
        setCardMoveModal({ open: true, cardMetadata })
      } else
        showToast({
          type: 'info',
          message:
            'Ainda não é possível realizar esta operação. Para mais informações, contate o suporte.',
        })
    }
  }

  function onCardClick(event, card) {
    if (loading) return
    event.stopPropagation()

    const serviceOrderId = card?.content?.id
    const serviceOrderRoute = useNewServiceOrder ? ROUTES.NEW_SERVICE_ORDER : ROUTES.SERVICE_ORDER
    window.open(`${serviceOrderRoute}/${serviceOrderId}`, '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    if (hasPermissionToViewPage) fetchKanbanData()
  }, [search, hasPermissionToViewPage])

  useEffect(() => {
    if (!hasPermissionToViewPage) setupPage()
  }, [hasPermissionToViewPage])

  useEffect(() => {
    fillColumns()
  }, [kanbanResults])

  if (!hasPermissionToViewPage) {
    return <Loader hasBackdrop open />
  }

  return (
    <Grid className={styles.container}>
      <HeaderTitle title="Chamados">
        <HeaderButtons />
      </HeaderTitle>
      <Grid className={styles.wrapper}>
        <Kanban
          configMap={configMap}
          onCardMove={onCardMove}
          onCardClick={onCardClick}
          onCardDragEnd={clearDragHighlight}
          onCardDndStateChange={handleCardDNDChange}
          renderSkeletonCard={() => <SkeletonCard />}
          columnWrapperStyle={columnWrapperStyle}
          dataSource={loading ? SKELETON_DATA : dataSource}
          renderColumnHeader={renderColumnHeader}
          columnStyle={columnStyle}
          rootStyle={ROOT_STYLE}
          renderColumnFooter={column => (
            <ColumnFooter column={column} minimizedColumns={minimizedColumns} />
          )}
        />
      </Grid>
      {cardMoveModal?.open && (
        <Suspense fallback={<Loader hasBackdrop open />}>
          <CardMoveModal
            cardMetadata={cardMoveModal?.cardMetadata}
            closeModal={() => setCardMoveModal({ open: false, cardMetadata: {} })}
          />
        </Suspense>
      )}
      {ManagerComponent}
    </Grid>
  )
}
